import type { Area } from '../areas/useAreas'

// The sub-area display name is computed once here and reused by BOTH the
// settings sheet's list and MapView's `subLabel` prop (map markers), so the
// two never disagree about what an area is called.

/**
 * Maps every sub area's id to its display name: the area's own name if it
 * has one, otherwise a numbered fallback ("<unnamedLabel> 1", "<unnamedLabel>
 * 2", …).
 *
 * The numbers are assigned by walking the sub areas sorted by DOCUMENT ID —
 * never by the array's fetch order and never by the eventual display order
 * (see sortSubAreasForDisplay) — so a given area keeps the same number across
 * reloads even when a different area is renamed or the API happens to return
 * areas in a different order. Only unnamed areas consume a number; a named
 * area never shifts the count for the unnamed ones that sort after it.
 */
export function buildSubAreaLabels(
  areas: readonly Area[],
  unnamedLabel: string,
): Map<string, string> {
  const byDocumentId = areas
    .filter((area) => area.areaType === 'sub')
    .slice()
    .sort((a, b) => a.id.localeCompare(b.id))

  const labels = new Map<string, string>()
  let unnamedCount = 0
  for (const area of byDocumentId) {
    if (area.name) {
      labels.set(area.id, area.name)
    } else {
      unnamedCount += 1
      labels.set(area.id, `${unnamedLabel} ${unnamedCount}`)
    }
  }
  return labels
}

/**
 * Sub areas in the order the settings sheet lists them: by display name,
 * case-insensitively, with Finnish collation (ä/ö sort correctly, "9" sorts
 * before "10") and a document-id tiebreak for areas that share a label —
 * production data has at least one duplicate-geometry pair ("Alue1" /
 * "asf etsintä 1"), which is an operator data-entry issue this list must not
 * hide or dedupe, only order stably.
 */
export function sortSubAreasForDisplay(
  areas: readonly Area[],
  labels: ReadonlyMap<string, string>,
): Area[] {
  return areas
    .filter((area) => area.areaType === 'sub')
    .slice()
    .sort((a, b) => {
      const labelA = labels.get(a.id) ?? ''
      const labelB = labels.get(b.id) ?? ''
      const byLabel = labelA.localeCompare(labelB, 'fi', { numeric: true, sensitivity: 'base' })
      if (byLabel !== 0) return byLabel
      return a.id.localeCompare(b.id)
    })
}
